import type { PhraseChoice, PhraseChoiceVariant, Speaker } from '@fluent/types';

/**
 * Pure helpers for the phrases course's `phrase_choice` exercise
 * (PHRASES.md § 5.1). One place answers "which language are the options",
 * "what does the sentence look like with the blank filled" and "which
 * English does this item carry" — the renderer (PhraseChoice.tsx), the
 * known-words set, the review blocks and the audio QA pages all read it.
 */

/** The blank in `gap` / `complete` prompts (validator: exactly one). */
export const BLANK = '___';

/** Options are Georgian meanings for `meaning`, English for every other variant. */
export function phraseChoiceOptionLang(variant: PhraseChoiceVariant): 'ka' | 'en' {
  return variant === 'meaning' ? 'ka' : 'en';
}

/** Variants whose prompt is English text with ONE `___` the pick fills. */
export function hasBlank(variant: PhraseChoiceVariant): boolean {
  return variant === 'gap' || variant === 'complete';
}

/** Variants whose prompt line is voiced by `prompt_audio_key` (auto-plays on mount). */
export function hasPromptAudio(ex: PhraseChoice): boolean {
  return (ex.variant === 'meaning' || ex.variant === 'reply') && Boolean(ex.prompt_audio_key && ex.prompt_en);
}

/** Replace the (first) blank with `fill`. Text without a blank comes back unchanged. */
export function fillBlank(text: string, fill: string): string {
  const at = text.indexOf(BLANK);
  return at < 0 ? text : text.slice(0, at) + fill + text.slice(at + BLANK.length);
}

/** One line of a `gap` prompt: "Mariam: Are you OK?" → who = "Mariam". */
export interface PromptLine {
  who: string | null;
  text: string;
}

/** A capitalised single word + colon at the start of a line is the speaker
 *  (same pattern as scripts/tts-text.ts SPEAKER_LABEL). */
const SPEAKER_LABEL = /^\s*([A-Z][\p{L}'\u2019-]*):\s+/u;

/**
 * Split a prompt into its lines (`\n` = a 2-line exchange) and peel off the
 * "Name: " speaker label each line may start with.
 */
export function promptLines(text: string): PromptLine[] {
  return text.split('\n').map((line) => {
    const m = SPEAKER_LABEL.exec(line);
    return m ? { who: m[1] ?? null, text: line.slice(m[0].length) } : { who: null, text: line };
  });
}

/** The prompt as one plain English sentence: answer in the blank, speaker labels and line breaks gone. */
export function resolvedPromptEn(ex: PhraseChoice): string | null {
  if (!ex.prompt_en) return null;
  const text = hasBlank(ex.variant) ? fillBlank(ex.prompt_en, ex.answer) : ex.prompt_en;
  return promptLines(text)
    .map((l) => l.text.trim())
    .filter(Boolean)
    .join(' ');
}

/**
 * What the item's after-the-verdict clip (`audio_key`) says — the app-side
 * twin of the TTS pipeline's rule (scripts/tts-text.ts `phraseChoiceAudioText`):
 * gap / complete → only the line that holds the blank, answer in place,
 * without its "Name: " label (a clip must not read the names and both turns
 * in one voice); every other variant → the answer; `meaning` → nothing: its
 * answer is Georgian and the validator rejects an audio_key there.
 */
export function phraseChoiceAudioText(ex: PhraseChoice): string | null {
  if (ex.variant === 'meaning') return null;
  if (hasBlank(ex.variant)) {
    if (!ex.prompt_en) return null;
    const line = ex.prompt_en.split('\n').find((l) => l.includes(BLANK)) ?? ex.prompt_en;
    return fillBlank(promptLines(line)[0]?.text ?? line, ex.answer).trim();
  }
  return ex.answer;
}

/** One clip of the item, as the audio QA pages list it. */
export interface PhraseChoiceClip {
  key: string;
  /** The English the clip says. */
  en: string;
  /** Georgian shown next to it. */
  ka: string;
  /** The character whose voice reads it; absent = the narrator. */
  speaker?: Speaker;
}

/**
 * Every clip the item references, in play order — mirrors scripts/tts-text.ts
 * `exerciseClips`: the prompt clip says `prompt_en` (meaning / reply; read in
 * the speaker's voice when the item has one), the answer clip says
 * `phraseChoiceAudioText`.
 */
export function phraseChoiceClips(ex: PhraseChoice): PhraseChoiceClip[] {
  const out: PhraseChoiceClip[] = [];
  if (ex.prompt_audio_key && ex.prompt_en) {
    out.push({
      key: ex.prompt_audio_key,
      en: ex.prompt_en,
      ka: (ex.variant === 'meaning' ? ex.answer : ex.after_ka) ?? '',
      speaker: ex.speaker,
    });
  }
  const after = phraseChoiceAudioText(ex);
  if (ex.audio_key && after !== null) {
    out.push({ key: ex.audio_key, en: after, ka: ex.after_ka ?? ex.prompt_ka ?? '' });
  }
  return out;
}

/**
 * Every ENGLISH string the item shows — never Georgian. The prompt with the
 * blank filled (so no `___`), and the answer + options when they are English.
 * Phrase-course options are real English by rule (taught phrases or plain
 * literal English, PHRASES.md § 7.5), so they are words a learner sees.
 */
export function phraseChoiceEnglishTexts(ex: PhraseChoice): string[] {
  const out: string[] = [];
  if (ex.prompt_en) {
    const text = hasBlank(ex.variant) ? fillBlank(ex.prompt_en, ex.answer) : ex.prompt_en;
    for (const line of promptLines(text)) if (line.text.trim()) out.push(line.text);
  }
  if (phraseChoiceOptionLang(ex.variant) === 'en') {
    out.push(ex.answer);
    for (const opt of ex.options) if (opt !== ex.answer) out.push(opt);
  }
  return out;
}
